// WebServicesPage.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Code, Globe2, Server, Database, LayoutDashboard, Layers, ShoppingCart,
  MessageSquare, Lock, Zap, BookOpen, TrendingUp, ShieldCheck, BarChart2, Package
} from 'lucide-react';

export default function WebServicesPage() {
  const [hoveredItem, setHoveredItem] = useState(null);

  const services = [
    {
      id: 'mern-development',
      title: 'MERN Stack Development',
      icon: Code,
      description: 'Full-stack apps using MongoDB, Express, React, and Node.js',
      gradient: 'linear-gradient(135deg, #4ade80, #16a34a)',
      route: '/web-services/mern-development'
    },
    {
      id: 'nextjs-websites',
      title: 'Next.js SSR Websites',
      icon: Globe2,
      description: 'SEO-first React websites with SSR, SSG, and ISR',
      gradient: 'linear-gradient(135deg, #60a5fa, #2563eb)',
      route: '/web-services/nextjs-websites'
    },
    {
      id: 'api-integration',
      title: 'API Integration & Backend',
      icon: Server,
      description: 'Build REST/GraphQL APIs and backend logic in Node.js',
      gradient: 'linear-gradient(135deg, #facc15, #eab308)',
      route: '/web-services/api-integration'
    },
    {
      id: 'database-management',
      title: 'Database Design & Management',
      icon: Database,
      description: 'Optimize MongoDB schema, queries, and indexing',
      gradient: 'linear-gradient(135deg, #f472b6, #db2777)',
      route: '/web-services/database-management'
    },
    {
      id: 'admin-dashboards',
      title: 'Admin Dashboards',
      icon: LayoutDashboard,
      description: 'Interactive dashboards with role-based controls',
      gradient: 'linear-gradient(135deg, #34d399, #059669)',
      route: '/web-services/admin-dashboards'
    },
    {
      id: 'microservices-architecture',
      title: 'Microservices & Deployment',
      icon: Layers,
      description: 'Scalable apps with Docker, CI/CD, and microservices',
      gradient: 'linear-gradient(135deg, #a78bfa, #7c3aed)',
      route: '/web-services/microservices-architecture'
    },
    {
      id: 'ecommerce-solutions',
      title: 'E-commerce Solutions',
      icon: ShoppingCart,
      description: 'Full-featured shopping cart and payment systems',
      gradient: 'linear-gradient(135deg, #fb923c, #ea580c)',
      route: '/web-services/ecommerce-solutions'
    },
    {
      id: 'realtime-apps',
      title: 'Real-time Applications',
      icon: MessageSquare,
      description: 'Live chat, notifications, or dashboards using WebSockets',
      gradient: 'linear-gradient(135deg, #06b6d4, #0e7490)',
      route: '/web-services/realtime-apps'
    },
    {
      id: 'auth-systems',
      title: 'Authentication Systems',
      icon: Lock,
      description: 'Secure login with JWT, OAuth, RBAC, and refresh tokens',
      gradient: 'linear-gradient(135deg, #f87171, #b91c1c)',
      route: '/web-services/auth-systems'
    },
    {
      id: 'performance-optimization',
      title: 'Performance Optimization',
      icon: Zap,
      description: 'Boost performance, reduce load times, and improve SEO',
      gradient: 'linear-gradient(135deg, #fcd34d, #fbbf24)',
      route: '/web-services/performance-optimization'
    },
    
    {
      id: 'pwa-development',
      title: 'Progressive Web Apps (PWA)',
      icon: Package,
      description: 'Installable apps with offline support and push notifications',
      gradient: 'linear-gradient(135deg, #22d3ee, #06b6d4)',
      route: '/web-services/pwa-development'
    },
    {
      id: 'seo-optimization',
      title: 'SEO Optimization',
      icon: TrendingUp,
      description: 'Structured data, sitemap, meta tags, and Open Graph',
      gradient: 'linear-gradient(135deg, #84cc16, #65a30d)',
      route: '/web-services/seo-optimization'
    },
    {
      id: 'deployment-services',
      title: 'Deployment & Hosting',
      icon: ShieldCheck,
      description: 'CI/CD pipelines and deployments to Vercel, Netlify, VPS',
      gradient: 'linear-gradient(135deg, #c084fc, #9333ea)',
      route: '/web-services/deployment-services'
    },
    {
      id: 'custom-crm',
      title: 'Custom CRM Development',
      icon: BarChart2,
      description: 'CRM solutions tailored for your business workflows',
      gradient: 'linear-gradient(135deg, #14b8a6, #0f766e)',
      route: '/web-services/custom-crm'
    }
  ];

  const containerStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
    padding: '80px 16px',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '64px',
    maxWidth: '1200px',
    margin: '0 auto 64px auto'
  };

  const iconContainerStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '80px',
    height: '80px',
    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
    borderRadius: '50%',
    marginBottom: '24px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  };

  const titleStyle = {
    fontSize: '3.5rem',
    fontWeight: '900',
    background: 'linear-gradient(135deg, #1f2937, #22c55e, #16a34a)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '24px',
    lineHeight: '1.1'
  };

  const subtitleStyle = {
    fontSize: '1.25rem',
    color: '#4b5563',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: '1.6'
  };

  const dividerStyle = {
    width: '96px',
    height: '4px',
    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
    margin: '32px auto 0 auto',
    borderRadius: '2px'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '32px',
    maxWidth: '1400px',
    margin: '0 auto'
  };

  const getCardStyle = (serviceId) => ({
    position: 'relative',
    height: '100%',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '16px',
    padding: '32px',
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    transform: hoveredItem === serviceId ? 'scale(1.05)' : 'scale(1)',
    backgroundColor: hoveredItem === serviceId ? '#f8fafc' : '#ffffff',
    borderColor: hoveredItem === serviceId ? '#cbd5e1' : '#e5e7eb'
  });

  const getIconStyle = (gradient) => ({
    width: '64px',
    height: '64px',
    background: gradient,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '24px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    transform: hoveredItem ? 'scale(1.1)' : 'scale(1)',
    transition: 'transform 0.3s ease'
  });

  const serviceTitleStyle = {
    fontSize: '1.25rem',
    fontWeight: '700',
    color: '#1f2937',
    marginBottom: '12px'
  };

  const serviceDescStyle = {
    fontSize: '0.875rem',
    color: '#6b7280',
    lineHeight: '1.5',
    marginBottom: '24px'
  };

  const learnMoreStyle = {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#22c55e'
  };

  const ctaContainerStyle = {
    textAlign: 'center',
    marginTop: '80px',
    maxWidth: '1200px',
    margin: '80px auto 0 auto'
  };

  const ctaBoxStyle = {
    background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
    border: '1px solid #e5e7eb',
    borderRadius: '16px',
    padding: '48px 32px',
    maxWidth: '600px',
    margin: '0 auto',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
  };

  const ctaTitleStyle = {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1f2937',
    marginBottom: '16px'
  };

  const ctaTextStyle = {
    fontSize: '1.125rem',
    color: '#4b5563',
    marginBottom: '32px'
  };

  const buttonStyle = {
    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
    color: '#ffffff',
    fontWeight: '700',
    padding: '16px 32px',
    borderRadius: '12px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease',
    textDecoration: 'none',
    display: 'inline-block'
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={iconContainerStyle}>
          <Code style={{ width: '40px', height: '40px', color: '#ffffff' }} />
        </div>
        <h1 style={titleStyle}>
          Web Development<br />
          <span style={{ fontSize: '2.5rem' }}>Services</span>
        </h1>
        <p style={subtitleStyle}>
          MERN + Next.js solutions tailored for modern, scalable, and high-performing applications that drive business growth and user engagement
        </p>
        <div style={dividerStyle}></div>
      </div>

      <div style={gridStyle}>
        {services.map((service, index) => {
          const Icon = service.icon;
          return (
            <Link
              to={service.route}
              key={service.id}
              style={getCardStyle(service.id)}
              onMouseEnter={() => setHoveredItem(service.id)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <div style={getIconStyle(service.gradient)}>
                <Icon style={{ width: '32px', height: '32px', color: '#ffffff' }} />
              </div>

              <div>
                <h3 style={serviceTitleStyle}>
                  {service.title}
                </h3>
                <p style={serviceDescStyle}>
                  {service.description}
                </p>
                
                <div style={learnMoreStyle}>
                  <span>Learn More</span>
                  <svg style={{ width: '16px', height: '16px', marginLeft: '8px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <div style={ctaContainerStyle}>
        <div style={ctaBoxStyle}>
          <h2 style={ctaTitleStyle}>
            Ready to Build Your Next Web Application?
          </h2>
          <p style={ctaTextStyle}>
            Discover how our MERN and Next.js expertise can bring your vision to life.
          </p>
          <Link to="/contact" style={buttonStyle}>
            GET STARTED →
          </Link>
        </div>
      </div>
    </div>
  );
}